// Pure geometry for "is an object covered by the union of the bounding boxes".
//
// Bounding boxes constrain where artwork may be placed. Historically an object
// had to fit entirely inside ONE box; now the constraint is the union of all
// boxes on the current side, so an element may span several boxes where they
// touch or overlap - but never bridge an uncovered gap between them.
//
// Testing that cannot be done by checking the object's four corners: with two
// boxes and a gap between them, all four corners can sit inside boxes while the
// object's midsection crosses the gap. Instead we decompose the boxes into
// pairwise-disjoint rectangles once, then compare areas: because the pieces are
// disjoint, area(Q n union) is simply the sum of area(Q n piece).
//
// No imports on purpose - this module is plain math and is unit tested directly.

const EPS = 1e-9;

/** @typedef {{ left:number, top:number, width:number, height:number, id?:string }} Box */
/** @typedef {{ x0:number, y0:number, x1:number, y1:number }} Rect */
/** @typedef {{ x:number, y:number }} Pt */

// An empty region means "no boxes" which historically meant "no constraint",
// so every coverage query against it answers true.
export const EMPTY_REGION = {
	rects: [],
	componentOfRect: [],
	componentCount: 0,
	bounds: { x0: 0, y0: 0, x1: 0, y1: 0 },
	isEmpty: true
};

// --- polygon helpers -------------------------------------------------------

// Shoelace. Absolute value, so winding order doesn't matter to callers.
export function polygonArea(pts) {
	const n = pts.length;
	if (n < 3) return 0;
	let sum = 0;
	for (let i = 0, j = n - 1; i < n; j = i++) {
		sum += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
	}
	return Math.abs(sum) / 2;
}

export function polygonBounds(pts) {
	let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
	for (const p of pts) {
		if (p.x < x0) x0 = p.x;
		if (p.y < y0) y0 = p.y;
		if (p.x > x1) x1 = p.x;
		if (p.y > y1) y1 = p.y;
	}
	return { x0, y0, x1, y1 };
}

export function scalePolygon(pts, anchor, s, out) {
	const dst = out || [];
	dst.length = pts.length;
	for (let i = 0; i < pts.length; i++) {
		dst[i] = {
			x: anchor.x + (pts[i].x - anchor.x) * s,
			y: anchor.y + (pts[i].y - anchor.y) * s
		};
	}
	return dst;
}

export function translatePolygon(pts, dx, dy, out) {
	const dst = out || [];
	dst.length = pts.length;
	for (let i = 0; i < pts.length; i++) {
		dst[i] = { x: pts[i].x + dx, y: pts[i].y + dy };
	}
	return dst;
}

// --- convex clipping -------------------------------------------------------

// Sutherland-Hodgman against the four half-planes of an axis-aligned rect.
// Exact for a convex subject polygon, which is all we ever pass (a fabric
// object's four rotated corners are always a convex quad).
//
// `scratch` lets hot callers avoid allocating on every frame.
export function clipConvexToRect(pts, rect, scratch) {
	let input = pts;
	let output = (scratch && scratch[0]) || [];
	let spare = (scratch && scratch[1]) || [];

	// edge: 0=left(x>=x0) 1=right(x<=x1) 2=top(y>=y0) 3=bottom(y<=y1)
	for (let edge = 0; edge < 4; edge++) {
		output.length = 0;
		const n = input.length;
		if (n === 0) return output;

		for (let i = 0, j = n - 1; i < n; j = i++) {
			const cur = input[i];
			const prev = input[j];
			const curIn = insideEdge(cur, rect, edge);
			const prevIn = insideEdge(prev, rect, edge);

			if (curIn) {
				if (!prevIn) output.push(intersectEdge(prev, cur, rect, edge));
				output.push(cur);
			} else if (prevIn) {
				output.push(intersectEdge(prev, cur, rect, edge));
			}
		}

		// Ping-pong the buffers so we never allocate inside the loop. `input`
		// starts as the caller's array, which must not be reused as scratch.
		const next = (input === pts) ? spare : input;
		input = output;
		output = next;
	}
	return input;
}

function insideEdge(p, rect, edge) {
	switch (edge) {
		case 0: return p.x >= rect.x0;
		case 1: return p.x <= rect.x1;
		case 2: return p.y >= rect.y0;
		default: return p.y <= rect.y1;
	}
}

function intersectEdge(a, b, rect, edge) {
	if (edge === 0 || edge === 1) {
		const x = edge === 0 ? rect.x0 : rect.x1;
		const t = (x - a.x) / (b.x - a.x);
		return { x, y: a.y + t * (b.y - a.y) };
	}
	const y = edge === 2 ? rect.y0 : rect.y1;
	const t = (y - a.y) / (b.y - a.y);
	return { x: a.x + t * (b.x - a.x), y };
}

// --- decomposition ---------------------------------------------------------

// Split the boxes into rectangles with pairwise-disjoint interiors covering the
// exact same area. Sweep over every distinct x edge: within a slab each box
// either fully spans it or misses it entirely (there is no partial case, since
// the slab boundaries are drawn from the box edges themselves), so the slab's
// cross-section is a fixed set of y-intervals that we merge.
export function buildRegion(boxes) {
	const src = [];
	for (const b of boxes || []) {
		if (!b) continue;
		const w = Number(b.width);
		const h = Number(b.height);
		const l = Number(b.left);
		const t = Number(b.top);
		if (!isFinite(w) || !isFinite(h) || !isFinite(l) || !isFinite(t)) continue;
		if (w <= 0 || h <= 0) continue; // degenerate boxes contribute nothing
		src.push({ x0: l, y0: t, x1: l + w, y1: t + h });
	}
	if (src.length === 0) return EMPTY_REGION;

	const xs = Array.from(new Set(src.flatMap(r => [r.x0, r.x1]))).sort((a, b) => a - b);

	// One merged y-interval list per slab.
	const slabs = [];
	for (let k = 0; k < xs.length - 1; k++) {
		const xa = xs[k];
		const xb = xs[k + 1];
		if (xb - xa <= EPS) continue;

		const spans = [];
		for (const r of src) {
			if (r.x0 <= xa + EPS && r.x1 >= xb - EPS) spans.push([r.y0, r.y1]);
		}
		if (spans.length === 0) continue;

		spans.sort((a, b) => a[0] - b[0]);
		const merged = [];
		let [cy0, cy1] = spans[0];
		for (let i = 1; i < spans.length; i++) {
			const [sy0, sy1] = spans[i];
			if (sy0 <= cy1 + EPS) {
				if (sy1 > cy1) cy1 = sy1;
			} else {
				merged.push([cy0, cy1]);
				cy0 = sy0;
				cy1 = sy1;
			}
		}
		merged.push([cy0, cy1]);
		slabs.push({ xa, xb, merged });
	}
	if (slabs.length === 0) return EMPTY_REGION;

	// Adjacent slabs with an identical cross-section merge into one wider rect.
	// Fewer pieces means fewer clips per frame.
	const rects = [];
	let run = slabs[0];
	for (let i = 1; i <= slabs.length; i++) {
		const nxt = slabs[i];
		const contiguous = nxt && Math.abs(nxt.xa - run.xb) <= EPS && sameSpans(run.merged, nxt.merged);
		if (contiguous) {
			run = { xa: run.xa, xb: nxt.xb, merged: run.merged };
			continue;
		}
		for (const [y0, y1] of run.merged) {
			rects.push({ x0: run.xa, y0, x1: run.xb, y1 });
		}
		run = nxt;
	}

	return {
		rects,
		...connectedComponents(rects),
		bounds: rectsBounds(rects),
		isEmpty: rects.length === 0
	};
}

function sameSpans(a, b) {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (Math.abs(a[i][0] - b[i][0]) > EPS || Math.abs(a[i][1] - b[i][1]) > EPS) return false;
	}
	return true;
}

function rectsBounds(rects) {
	let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
	for (const r of rects) {
		if (r.x0 < x0) x0 = r.x0;
		if (r.y0 < y0) y0 = r.y0;
		if (r.x1 > x1) x1 = r.x1;
		if (r.y1 > y1) y1 = r.y1;
	}
	return { x0, y0, x1, y1 };
}

// Union-find over rects that share a positive-length edge. Used to place one
// "add" affordance per contiguous printable area rather than one per box.
function connectedComponents(rects) {
	const parent = rects.map((_, i) => i);
	const find = i => {
		while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
		return i;
	};
	const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

	for (let i = 0; i < rects.length; i++) {
		for (let j = i + 1; j < rects.length; j++) {
			if (touches(rects[i], rects[j])) union(i, j);
		}
	}

	const label = new Map();
	const componentOfRect = rects.map((_, i) => {
		const root = find(i);
		if (!label.has(root)) label.set(root, label.size);
		return label.get(root);
	});
	return { componentOfRect, componentCount: label.size };
}

// Shared border of non-zero length (touching corners don't connect).
function touches(a, b) {
	const xOverlap = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
	const yOverlap = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
	if (xOverlap > EPS && Math.abs(a.y1 - b.y0) <= EPS) return true;
	if (xOverlap > EPS && Math.abs(b.y1 - a.y0) <= EPS) return true;
	if (yOverlap > EPS && Math.abs(a.x1 - b.x0) <= EPS) return true;
	if (yOverlap > EPS && Math.abs(b.x1 - a.x0) <= EPS) return true;
	return xOverlap > EPS && yOverlap > EPS; // overlapping (shouldn't happen, but harmless)
}

// --- coverage --------------------------------------------------------------

const _scratch = [[], []];

export function coveredArea(region, pts) {
	if (!region || region.isEmpty) return 0;
	const qb = polygonBounds(pts);
	let total = 0;
	for (const r of region.rects) {
		if (r.x1 <= qb.x0 || r.x0 >= qb.x1 || r.y1 <= qb.y0 || r.y0 >= qb.y1) continue;
		total += polygonArea(clipConvexToRect(pts, r, _scratch));
	}
	return total;
}

// True when every part of the convex polygon sits inside the region.
export function isPolygonCovered(region, pts, eps) {
	if (!region || region.isEmpty) return true; // no boxes => no constraint
	const total = polygonArea(pts);
	if (total <= 0) return true;

	const tol = eps != null ? eps : Math.max(1e-3, total * 1e-6);
	const qb = polygonBounds(pts);
	let remaining = total;

	for (const r of region.rects) {
		if (r.x1 <= qb.x0 || r.x0 >= qb.x1 || r.y1 <= qb.y0 || r.y0 >= qb.y1) continue;
		remaining -= polygonArea(clipConvexToRect(pts, r, _scratch));
		if (remaining <= tol) return true;
	}
	return remaining <= tol;
}

export function uncoveredArea(region, pts) {
	return Math.max(0, polygonArea(pts) - coveredArea(region, pts));
}

// --- scale search ----------------------------------------------------------

// Largest uniform scale about `anchor` whose polygon is still covered.
//
// Coverage is monotone non-increasing in s: for s1 < s2 the smaller polygon is
// contained in the larger, so if the larger fits the smaller does too. That
// holds for any region shape, which is what makes bisection exact here rather
// than a heuristic. Valid because the editor only exposes uniform scaling.
export function maxScaleThatFits(region, pts, anchor, opts) {
	const o = opts || {};
	// The search never returns a mirrored scale: the monotonicity argument above
	// only holds for s >= 0, and a negative s is a flip, not a smaller polygon.
	let lo = Math.max(0, o.lo != null ? o.lo : 0);
	let hi = Math.max(lo, o.hi != null ? o.hi : 1);
	const iterations = o.iterations != null ? o.iterations : 22;

	if (!region || region.isEmpty) return hi;

	const buf = [];
	if (isPolygonCovered(region, scalePolygon(pts, anchor, hi, buf))) return hi;

	for (let i = 0; i < iterations; i++) {
		const mid = (lo + hi) / 2;
		if (isPolygonCovered(region, scalePolygon(pts, anchor, mid, buf))) lo = mid;
		else hi = mid;
	}
	return lo;
}

// --- point / component queries --------------------------------------------

export function pointInRegion(region, x, y) {
	if (!region || region.isEmpty) return false;
	for (const r of region.rects) {
		if (x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1) return true;
	}
	return false;
}

export function componentAt(region, x, y) {
	if (!region || region.isEmpty) return -1;
	for (let i = 0; i < region.rects.length; i++) {
		const r = region.rects[i];
		if (x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1) return region.componentOfRect[i];
	}
	return -1;
}

// Biggest piece of a component. Its centre is guaranteed to be inside the
// region, unlike the component's centroid which can land in a gap.
export function largestRectOfComponent(region, component) {
	if (!region || region.isEmpty) return null;
	let best = null;
	let bestArea = -1;
	for (let i = 0; i < region.rects.length; i++) {
		if (region.componentOfRect[i] !== component) continue;
		const r = region.rects[i];
		const area = (r.x1 - r.x0) * (r.y1 - r.y0);
		if (area > bestArea) { bestArea = area; best = r; }
	}
	return best;
}

// --- box groups ------------------------------------------------------------

// Boxes that overlap or share a border describe one contiguous printable area,
// so the UI shows them as a single region: one outline around the union instead
// of a rectangle per box, and one insert affordance anchored on the biggest box
// of the group. Boxes separated by a gap stay separate groups.
//
// Returns one entry per group: its boxes, the biggest of them, the outline of
// the union as line segments, and the union's bounds.
export function groupBoxes(boxes) {
	const src = [];
	for (const b of boxes || []) {
		if (!b) continue;
		const w = Number(b.width);
		const h = Number(b.height);
		const l = Number(b.left);
		const t = Number(b.top);
		if (!isFinite(w) || !isFinite(h) || !isFinite(l) || !isFinite(t)) continue;
		if (w <= 0 || h <= 0) continue; // degenerate boxes contribute nothing
		src.push({ box: b, rect: { x0: l, y0: t, x1: l + w, y1: t + h } });
	}
	if (src.length === 0) return [];

	const parent = src.map((_, i) => i);
	const find = i => {
		while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
		return i;
	};
	for (let i = 0; i < src.length; i++) {
		for (let j = i + 1; j < src.length; j++) {
			if (!touches(src[i].rect, src[j].rect)) continue;
			const ra = find(i), rb = find(j);
			if (ra !== rb) parent[ra] = rb;
		}
	}

	// Keep the input order so the first box of a side keeps leading its group.
	const byRoot = new Map();
	for (let i = 0; i < src.length; i++) {
		const root = find(i);
		if (!byRoot.has(root)) byRoot.set(root, []);
		byRoot.get(root).push(src[i]);
	}

	const groups = [];
	for (const members of byRoot.values()) {
		const rects = members.map(m => m.rect);
		let largest = members[0];
		for (const m of members) {
			if (rectArea(m.rect) > rectArea(largest.rect)) largest = m;
		}
		groups.push({
			boxes: members.map(m => m.box),
			largest: largest.box,
			outline: unionOutline(rects),
			bounds: rectsBounds(rects)
		});
	}
	return groups;
}

function rectArea(r) {
	return (r.x1 - r.x0) * (r.y1 - r.y0);
}

// The group a point falls in, or null. Ties go to the first group, matching
// boxAtPoint's "first box wins" for overlapping boxes.
export function groupAtPoint(groups, x, y) {
	for (const g of groups || []) {
		for (const r of g.boxes) {
			if (x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height) return g;
		}
	}
	return null;
}

// Outline of the union of (possibly overlapping) rects, as axis-aligned
// segments. A stretch of a rect's edge belongs to the outline when the strip
// just outside it is covered by no rect - that test needs no decomposition and
// handles overlaps, shared edges and duplicates alike.
//
// Collinear stretches are merged so a run of boxes draws as one dashed line
// rather than several with restarting dash phases.
export function unionOutline(rects) {
	// key: `${orientation}|${coordinate}` -> intervals along the edge
	const lines = new Map();
	const addInterval = (orient, coord, lo, hi) => {
		const key = `${orient}|${coord}`;
		if (!lines.has(key)) lines.set(key, { orient, coord, spans: [] });
		lines.get(key).spans.push([lo, hi]);
	};

	for (const r of rects) {
		addOutlineParts(rects, 'v', r.x0, r.y0, r.y1, -1, addInterval);
		addOutlineParts(rects, 'v', r.x1, r.y0, r.y1, +1, addInterval);
		addOutlineParts(rects, 'h', r.y0, r.x0, r.x1, -1, addInterval);
		addOutlineParts(rects, 'h', r.y1, r.x0, r.x1, +1, addInterval);
	}

	const segments = [];
	for (const line of lines.values()) {
		for (const [lo, hi] of mergeSpans(line.spans)) {
			if (line.orient === 'v') segments.push({ x1: line.coord, y1: lo, x2: line.coord, y2: hi });
			else segments.push({ x1: lo, y1: line.coord, x2: hi, y2: line.coord });
		}
	}
	return segments;
}

// The probe sits just outside the edge, so a rect only ever hides a neighbour's
// edge, never its own.
const PROBE = 1e-4;

function addOutlineParts(rects, orient, coord, lo, hi, outward, emit) {
	const probe = coord + outward * PROBE;
	const covered = [];
	for (const q of rects) {
		if (orient === 'v') {
			if (q.x0 <= probe && q.x1 >= probe) covered.push([q.y0, q.y1]);
		} else {
			if (q.y0 <= probe && q.y1 >= probe) covered.push([q.x0, q.x1]);
		}
	}
	for (const [a, b] of subtractSpans(lo, hi, covered)) emit(orient, coord, a, b);
}

// [lo, hi] minus the union of `spans`, dropping slivers.
function subtractSpans(lo, hi, spans) {
	let out = [[lo, hi]];
	for (const [a, b] of mergeSpans(spans)) {
		const next = [];
		for (const [c, d] of out) {
			if (b <= c + EPS || a >= d - EPS) { next.push([c, d]); continue; }
			if (a > c + EPS) next.push([c, a]);
			if (b < d - EPS) next.push([b, d]);
		}
		out = next;
		if (out.length === 0) break;
	}
	return out.filter(([a, b]) => b - a > EPS);
}

// Union of intervals; touching intervals merge into one.
function mergeSpans(spans) {
	if (spans.length === 0) return [];
	const sorted = spans.slice().sort((a, b) => a[0] - b[0]);
	const out = [];
	let [cur0, cur1] = sorted[0];
	for (let i = 1; i < sorted.length; i++) {
		const [s0, s1] = sorted[i];
		if (s0 <= cur1 + EPS) {
			if (s1 > cur1) cur1 = s1;
		} else {
			out.push([cur0, cur1]);
			cur0 = s0;
			cur1 = s1;
		}
	}
	out.push([cur0, cur1]);
	return out;
}

// --- recovery candidates ---------------------------------------------------

// Centre positions worth trying when an object has ended up outside the region.
// For each piece we clamp the object's current centre into the range that keeps
// its bounding rect inside that piece - the same arithmetic the old per-box
// clamp used, now applied per disjoint piece. Nearest candidates come first.
//
// This is an approximation: the exact nearest valid pose is the erosion of the
// region by the rotated quad, which needs general polygon booleans. Callers
// fall back to shrinking, so termination is still guaranteed.
export function candidateCenters(region, pts) {
	if (!region || region.isEmpty) return [];
	const b = polygonBounds(pts);
	const hw = (b.x1 - b.x0) / 2;
	const hh = (b.y1 - b.y0) / 2;
	const cx = (b.x0 + b.x1) / 2;
	const cy = (b.y0 + b.y1) / 2;

	const out = [];
	for (const r of region.rects) {
		const minX = r.x0 + hw;
		const maxX = r.x1 - hw;
		const minY = r.y0 + hh;
		const maxY = r.y1 - hh;
		// Wider than the piece on an axis => centre on that axis.
		const x = minX > maxX ? (r.x0 + r.x1) / 2 : Math.min(Math.max(cx, minX), maxX);
		const y = minY > maxY ? (r.y0 + r.y1) / 2 : Math.min(Math.max(cy, minY), maxY);
		out.push({ x, y, dist: (x - cx) * (x - cx) + (y - cy) * (y - cy) });
	}
	out.sort((p, q) => p.dist - q.dist);
	return out;
}
